import type { PulseFinding } from '../../../api/work';
import './insights.css';

/**
 * One finding, opened out: why it was raised and what it was raised from.
 *
 * The predecessor showed four sections — Narrative, Suggested check,
 * Evidence, Tool calls (audit) — and the last is the one that makes the
 * rest trustworthy. `work.pulse` composes findings with a model; the tool
 * calls are what it actually queried, so a reader can re-run them and
 * disagree. Without them a finding is an assertion.
 *
 * **`reasoning` is shown and labelled as the model's.** It is stored on the
 * row and is the model explaining itself, which is not the same kind of
 * claim as the evidence. Presenting the two identically would let a
 * plausible-sounding sentence borrow the authority of a ticket key.
 *
 * `evidence` and `tool_calls` arrive parsed and shaped by whatever produced
 * them, so they render as JSON rather than a schema this file invents.
 */

interface Props {
  finding: PulseFinding | null;
  onClose: () => void;
}

function isNonEmptyArray(v: unknown): v is unknown[] {
  return Array.isArray(v) && v.length > 0;
}

export function FindingSidePanel({ finding, onClose }: Props) {
  if (!finding) return null;
  return (
    <aside className="fsp" role="dialog" aria-label={finding.headline}>
      <header className="fsp__head">
        <div className="fsp__title">
          <span className={`in-badge in-badge--${finding.severity}`}>
            {finding.severity}
          </span>
          <span className="in-badge">{finding.lane_primary}</span>
          <span className="in-dim">
            {Math.round(finding.confidence * 100)}% confident · {finding.kind}
          </span>
        </div>
        <button type="button" className="fsp__close" onClick={onClose}>
          Close
        </button>
      </header>

      <h3 className="fsp__headline">{finding.headline}</h3>

      <section className="fsp__section">
        <span className="nb__label">Narrative</span>
        <p className="fsp__text">{finding.narrative}</p>
      </section>

      {finding.suggested_check && (
        <section className="fsp__section">
          <span className="nb__label">Suggested check</span>
          <p className="fsp__text">{finding.suggested_check}</p>
        </section>
      )}

      {isNonEmptyArray(finding.evidence) && (
        <section className="fsp__section">
          <span className="nb__label">
            Evidence · {finding.evidence.length}
          </span>
          <pre className="fsp__pre">
            {JSON.stringify(finding.evidence, null, 2)}
          </pre>
        </section>
      )}

      {isNonEmptyArray(finding.tool_calls) && (
        <section className="fsp__section">
          <span className="nb__label">
            Tool calls · {finding.tool_calls.length}
          </span>
          <p className="in-dim">
            What was actually queried to raise this. Re-run them to disagree
            with it.
          </p>
          <pre className="fsp__pre">
            {JSON.stringify(finding.tool_calls, null, 2)}
          </pre>
        </section>
      )}

      {finding.reasoning && (
        <section className="fsp__section">
          <span className="nb__label">Model's reasoning</span>
          <p className="in-dim">
            The model explaining itself — not evidence, and shown apart from
            it for that reason.
          </p>
          <p className="fsp__text fsp__text--quiet">{finding.reasoning}</p>
        </section>
      )}
    </aside>
  );
}
